import { useQuery } from '@tanstack/react-query'
import { workordersApi, type WorkOrder } from '../api/workorders'

// ── Map work order state to alert styling ─────────────────────────────────────

function alertType(wo: WorkOrder): 'error' | 'warning' | 'info' {
  if (wo.priority === 'urgent') return 'error'
  if (wo.priority === 'high')   return 'warning'
  return 'info'
}

const styles: Record<string, { dot: string; text: string; row: string }> = {
  error:   { dot: 'bg-red-500',   text: 'text-red-700',   row: 'hover:bg-red-50/50' },
  warning: { dot: 'bg-amber-400', text: 'text-amber-700', row: 'hover:bg-amber-50/50' },
  info:    { dot: 'bg-brand-cyan', text: 'text-brand-cyan-text', row: 'hover:bg-blue-50/50' },
}

const STATUS_LABEL: Record<string, string> = {
  pending:     'Na čekanju',
  assigned:    'Dodeljeno',
  in_progress: 'U toku',
  completed:   'Završeno',
  cancelled:   'Otkazano',
}

function timeAgo(iso: string): string {
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000)
  if (s < 60)   return 'Upravo'
  if (s < 3600) return `Pred ${Math.floor(s / 60)} min`
  if (s < 86400) return `Pred ${Math.floor(s / 3600)} h`
  return `Pred ${Math.floor(s / 86400)} d`
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function AlertsFeed() {
  const { data: workOrders, isLoading } = useQuery({
    queryKey: ['workorders-alerts'],
    queryFn: () =>
      workordersApi
        .list({ status: 'pending,assigned,in_progress', limit: 20 })
        .then((r) => r.data),
    refetchInterval: 60_000,
    staleTime: 30_000,
  })

  // Sort: urgent first, then high, then by created_at desc
  const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 }
  const sorted = [...(workOrders ?? [])].sort((a, b) => {
    const pd = (priorityOrder[a.priority as keyof typeof priorityOrder] ?? 3)
             - (priorityOrder[b.priority as keyof typeof priorityOrder] ?? 3)
    if (pd !== 0) return pd
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  })

  const alertCount = sorted.filter((w) => alertType(w) !== 'info').length

  return (
    <div className="bg-white rounded-xl shadow-xs border border-gray-100 flex flex-col h-full">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-900">Aktivni nalozi</h2>
        {alertCount > 0 && (
          <span className="text-xs bg-red-100 text-red-700 font-semibold px-2 py-0.5 rounded-full">
            {alertCount}
          </span>
        )}
      </div>

      <div className="divide-y divide-gray-50 overflow-y-auto flex-1">
        {isLoading && (
          <div className="px-5 py-8 text-center text-sm text-gray-400">Učitavanje…</div>
        )}

        {!isLoading && sorted.length === 0 && (
          <div className="px-5 py-8 text-center text-sm text-gray-400">
            Nema aktivnih radnih naloga
          </div>
        )}

        {sorted.map((wo) => {
          const type = alertType(wo)
          const s = styles[type]
          return (
            <div key={wo.id} className={`px-5 py-3.5 transition-colors ${s.row}`}>
              <div className="flex items-start gap-3">
                <span className={`mt-1.5 shrink-0 w-2 h-2 rounded-full ${s.dot}`} />
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-semibold ${s.text} leading-tight truncate`}>
                    {wo.title}
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5 leading-snug truncate">
                    {wo.address} — {STATUS_LABEL[wo.status] ?? wo.status}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">{timeAgo(wo.created_at)}</p>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
